import { env } from './env';

type CallOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | undefined>;
  signal?: AbortSignal;
};

export class FunctionError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'FunctionError';
    this.status = status;
  }
}

/**
 * Calls a Supabase Edge Function directly. We bypass `functions.invoke` so we
 * control timeouts, query strings, and error shape in one place.
 */
export async function callFunction<T>(name: string, options: CallOptions = {}): Promise<T> {
  if (!env) {
    throw new FunctionError('The store is not configured yet.', 503);
  }

  const url = new URL(`${env.VITE_SUPABASE_URL}/functions/v1/${name}`);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, value);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20_000);
  options.signal?.addEventListener('abort', () => controller.abort(), { once: true });

  try {
    const response = await fetch(url, {
      method: options.method ?? 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.supabaseKey,
        Authorization: `Bearer ${env.supabaseKey}`,
      },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: controller.signal,
    });

    const data = (await response.json().catch(() => ({}))) as T & { error?: string };

    if (!response.ok) {
      throw new FunctionError(
        typeof data?.error === 'string' ? data.error : 'Something went wrong. Please try again.',
        response.status,
      );
    }

    return data;
  } finally {
    clearTimeout(timeout);
  }
}
